import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Settings, ShieldCheck, User } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { pedirSalida } from '@/lib/salida-con-cambios';

/**
 * El menú de la cuenta: Mi perfil, Configuración, la puerta al panel de
 * plataforma y Cerrar sesión.
 *
 * Rediseño del 18-oct (artefacto): vive en la tarjeta del usuario, abajo del
 * menú lateral, y no en la barra de arriba. Mismas opciones y mismo cuidado:
 * cada salida pasa por `pedirSalida` (spec 294: con cambios sin aplicar,
 * pregunta antes).
 *
 * 🔴 La puerta al panel de plataforma es la ÚNICA alcanzable: con un solo
 * portal el ingreso salta derecho a `/<slug>/dashboard` y la pantalla de
 * portales no se muestra nunca. Estando ya adentro del panel no se ofrece.
 */
export function MenuDeLaCuenta({
  children,
  lado = 'top',
}: {
  /** Lo que abre el menú (la tarjeta del usuario, o su avatar con el menú achicado). */
  children: ReactNode;
  lado?: 'top' | 'right';
}) {
  const { user, logout, fullName } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const esDeLaPlataforma = user?.role === 'PLATFORM_ADMIN';
  const yaEnElPanel = location.pathname.startsWith('/plataforma');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent side={lado} align="start" sideOffset={8} className="w-64">
        <DropdownMenuLabel className="font-normal">
          <p className="truncate font-semibold">{fullName}</p>
          <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => pedirSalida(() => navigate('/profile'))}>
          <User className="mr-2 h-4 w-4" aria-hidden="true" />
          Mi Perfil
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => pedirSalida(() => navigate('/settings'))}>
          <Settings className="mr-2 h-4 w-4" aria-hidden="true" />
          Configuración
        </DropdownMenuItem>
        {esDeLaPlataforma && !yaEnElPanel && (
          <>
            <DropdownMenuSeparator />
            {/*
              🔴 Un `<a>` plano y no `navigate()` ni `<Link>`: adentro de un portal
              el router corre con `basename=/<slug>`, y los dos le anteponen ese
              prefijo. La recarga completa vuelve a resolver el basename, esta vez
              sin empresa.
            */}
            <DropdownMenuItem asChild>
              <a
                href="/plataforma"
                className="text-plataforma focus:bg-plataforma-suave focus:text-plataforma"
              >
                <ShieldCheck className="mr-2 h-4 w-4" aria-hidden="true" />
                Panel de plataforma · DATEC
              </a>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onSelect={() =>
            pedirSalida(() => {
              navigate('/login');
              logout();
            })
          }
        >
          <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
          Cerrar Sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
